// CSV export per project, the invoice summary and the printable report. The browser saves the CSV; the invoice and
// the report print to paper or PDF (the browser's "Save as PDF") from an A4 layout.
import { useEffect, useMemo, useState } from 'react';
import type { Interval, TokenCostLine } from '../../../packages/core/src/index.ts';
import type { StripeInvoice, StripeStatus } from '../api/types.ts';
import { dayRange, monthRange, weekRange } from '../lib/calendar.ts';
import { activeProjects, client, isOwner, runningEntry, tokenSettings, vatBasisPoints } from '../model/data.ts';
import { buildProjectExport, invoice, invoiceTokenLines, projectCsv, projectCsvName, stripeLines } from '../model/exports.ts';
import { buildReport } from '../model/report.ts';
import { Banner, download, Field, Figure, Loading, Select, Sheet, TextInput, Toggle } from '../components/common.tsx';
import { rangeLabel } from '../screens/Reports.tsx';
import { useFmt, useNow, useRange, useStore } from '../store/hooks.tsx';
import { Button } from '../ui/ds.tsx';

type ExportPeriod = 'today' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth';

export function ExportSheet({ projectId }: { projectId: string | null }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const now = useNow(60_000);
  const today = useRange(dayRange(now, store.timeZone)).data;
  const projects = activeProjects(store.ws);
  const [selected, setSelected] = useState(projectId ?? (today ? runningEntry(today)?.projectId : null) ?? projects[0]?.id ?? '');
  const [period, setPeriod] = useState<ExportPeriod>('thisWeek');
  const range: Interval = useMemo(() => {
    switch (period) {
      case 'today': return dayRange(now, store.timeZone);
      case 'thisWeek': return weekRange(now, store.timeZone);
      case 'lastWeek': return weekRange(now, store.timeZone, -1);
      case 'thisMonth': return monthRange(now, store.timeZone);
      case 'lastMonth': return monthRange(now, store.timeZone, -1);
    }
  }, [period, Math.floor(now / 3_600_000), store.timeZone]);
  // Owners bill the team's time; members export their own.
  const loaded = useRange(range, false, isOwner(store.ws));
  const x = loaded.data ? buildProjectExport(t, store.ws, loaded.data, selected, range, now) : null;
  const close = () => store.closeSheet();
  const labels: Record<ExportPeriod, string> = {
    today: t('Heute'), thisWeek: t('Diese Woche'), lastWeek: t('Letzte Woche'), thisMonth: t('Dieser Monat'), lastMonth: t('Letzter Monat'),
  };
  return (
    <Sheet title={t('CSV exportieren')} onClose={close} footer={<>
      <span className="spacer" />
      <Button variant="plain" onClick={close}>{t('Schließen')}</Button>
      <Button variant="primary" icon="download" disabled={!x || x.rows.length === 0} onClick={() => x && download(projectCsvName(x, store.timeZone), projectCsv(t, x, store.lang, store.timeZone))}>
        {t('Als CSV sichern …')}
      </Button>
    </>}>
      <div className="form-row">
        <Field label={t('Projekt')}>
          <Select value={selected} onChange={(e) => setSelected(e.target.value)}>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        </Field>
        <Field label={t('Zeitraum')}>
          <Select value={period} onChange={(e) => setPeriod(e.target.value as ExportPeriod)}>
            {(Object.keys(labels) as ExportPeriod[]).map((p) => <option key={p} value={p}>{labels[p]}</option>)}
          </Select>
        </Field>
      </div>
      {!loaded.data ? <Loading label={t('Lädt …')} /> : x ? (
        <div className="export-preview">
          {x.rows.length === 0 ? <p className="note">{t('Keine abrechenbare Zeit in diesem Zeitraum.')}</p> : (
            <>
              <div className="figures">
                <Figure label={t('Abgerechnet')} value={fmt.duration(x.billedMs)} strong />
                <Figure label={t('Summe')} value={fmt.duration(x.rawMs)} />
                <Figure label={t('Überschneidung')} value={fmt.duration(Math.max(0, x.rawMs - x.billedMs))} />
                {x.project.rateCents !== null ? <Figure label={t('Betrag')} value={fmt.money(x.rounded?.amountCents ?? 0, x.project.currency)} strong /> : null}
              </div>
              <p className="note">{`${t('%lld Intervalle', x.rows.length)}, ${x.project.roundingMinutes === 0 ? t('sekundengenau, ohne Rundung.')
                : t('je auf %lld min aufgerundet: %@.', x.project.roundingMinutes, fmt.duration(x.rounded?.roundedMs ?? 0))}`}</p>
            </>
          )}
        </div>
      ) : null}
      <p className="note note--faint">{t('Eine Zeile pro zusammengeführtem Intervall: deine Zeit und die Agent-Zeit dieses Projekts, Überschneidungen einmal. Korrekturen sind berücksichtigt.')}</p>
    </Sheet>
  );
}

function PrintButton() {
  const t = useStore().t;
  return <Button variant="primary" icon="doc" onClick={() => window.print()}>{t('Drucken oder als PDF sichern …')}</Button>;
}

export function InvoiceSheet({ range }: { range: Interval }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const now = useNow(60_000);
  // An invoice covers everyone's time on the client's projects.
  const owner = isOwner(store.ws);
  const tokenCosts = tokenSettings(store.ws);
  const loaded = useRange(range, tokenCosts !== null, owner);
  const [includeTokens, setIncludeTokens] = useState(true);
  const [clientId, setClientId] = useState<string>('all');
  const [number, setNumber] = useState('');
  const [finalize, setFinalize] = useState(true);
  const [stripe, setStripe] = useState<StripeStatus | null>(null);
  const [created, setCreated] = useState<StripeInvoice | null>(null);
  const [creating, setCreating] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // Stripe writes are idempotent per invoice id: a new client means a new invoice.
  const [invoiceId, setInvoiceId] = useState(() => crypto.randomUUID());
  useEffect(() => { if (owner) store.api.stripe().then(setStripe).catch(() => setStripe(null)); }, [store, owner]);
  const close = () => store.closeSheet();
  const bp = vatBasisPoints(store.ws);
  const report = loaded.data ? buildReport(t, store.ws, loaded.data, range, 'project', now, store.timeZone, false) : null;
  const tokens = loaded.data ? invoiceTokenLines(store.ws, loaded.data, range) : new Map<string, TokenCostLine>();
  // Clients with something to bill in the range; "all" keeps every project on one paper.
  const billed = new Set([...(report ? [...report.perProject].filter(([, x]) => x.billableMs > 0).map(([id]) => id) : []), ...tokens.keys()]);
  const clientIds = [...new Set(store.ws.projects.filter((p) => billed.has(p.id)).map((p) => p.clientId ?? ''))];
  const inScope = (projectId: string) => clientId === 'all' || (store.ws.projects.find((p) => p.id === projectId)?.clientId ?? '') === clientId;
  const perProject = new Map([...(report?.perProject ?? new Map())].filter(([id]) => inScope(id)));
  const scopedTokens = new Map([...tokens].filter(([id]) => inScope(id)));
  const summaries = report ? invoice(store.ws, perProject, bp, includeTokens ? scopedTokens : undefined) : [];
  const sender = store.ws.settings.get('invoiceSender') ?? '';
  const name = (id: string) => store.ws.projects.find((p) => p.id === id)?.name ?? t('Gelöschtes Projekt');
  const percent = (basisPoints: number) => fmt.number(basisPoints / 100, basisPoints % 100 === 0 ? 0 : 1);
  const chosenClient = clientId !== 'all' && clientId !== '' ? client(store.ws, clientId) : undefined;
  const summary = summaries[0];
  const stripeReady = !!stripe?.connected && !!chosenClient && summaries.length === 1 && !!summary && summary.netCents > 0;
  const createInStripe = async () => {
    if (!summary || !chosenClient) return;
    setCreating(true);
    setProblem(null);
    try {
      setCreated(await store.api.createInvoice({
        id: invoiceId, clientId: chosenClient.id, clientName: chosenClient.name, number: number.trim() || null,
        description: t('Leistungszeitraum %@', rangeLabel(store, range)), rangeStart: range.start, rangeEnd: range.end, currency: summary.currency,
        vatBasisPoints: summary.vatBasisPoints, exempt: bp === 0, lines: stripeLines(t, fmt, store.ws, summary), finalize,
      }));
    } catch (e) {
      setProblem(store.describe(e));
    } finally {
      setCreating(false);
    }
  };
  return (
    <Sheet title={t('Rechnungszusammenfassung')} subtitle={rangeLabel(store, range)} onClose={close} width={720} footer={<>
      <span className="spacer" />
      <Button variant="plain" onClick={close}>{t('Schließen')}</Button>
      {stripe?.connected ? (
        <Button icon="euro" disabled={!stripeReady || creating || created !== null} onClick={() => void createInStripe()}>{t('In Stripe anlegen')}</Button>
      ) : null}
      <PrintButton />
    </>}>
      <div className="invoice-options">
        {clientIds.length > 0 ? (
          <Field label={t('Kunde')}>
            <Select value={clientId} onChange={(e) => { setClientId(e.target.value); setCreated(null); setInvoiceId(crypto.randomUUID()); }}>
              <option value="all">{t('Alle Projekte')}</option>
              {clientIds.map((id) => <option key={id || 'none'} value={id}>{id ? client(store.ws, id)?.name ?? id : t('Ohne Kunde')}</option>)}
            </Select>
          </Field>
        ) : null}
        {stripe?.connected ? (
          <Field label={t('Rechnungsnummer')} hint={t('Leer: Stripe vergibt sie beim Abschließen.')}>
            <TextInput value={number} placeholder={t('optional')} onChange={(e) => setNumber(e.target.value)} />
          </Field>
        ) : null}
      </div>
      <div className="invoice-options">
        {tokens.size > 0 ? (
          <Field inline label={t('Token-Kosten als eigene Position')}><Toggle checked={includeTokens} onChange={setIncludeTokens} label={t('Token-Kosten als eigene Position')} /></Field>
        ) : null}
        {stripe?.connected ? (
          <Field inline label={t('In Stripe direkt abschließen')}><Toggle checked={finalize} onChange={setFinalize} label={t('In Stripe direkt abschließen')} /></Field>
        ) : null}
      </div>
      {stripe?.connected && !chosenClient && summaries.length > 0 ? <p className="note">{t('Für Stripe wähle einen Kunden: jede Rechnung geht an einen.')}</p> : null}
      {stripe?.connected && summaries.length > 1 ? <p className="note">{t('Die Projekte haben verschiedene Währungen: lege in Stripe eine Rechnung je Währung an.')}</p> : null}
      {problem ? <Banner tone="danger">{problem}</Banner> : null}
      {created ? (
        <Banner icon="check" actions={created.dashboardUrl ? <a className="linklike" href={created.dashboardUrl} target="_blank" rel="noreferrer">{t('In Stripe öffnen')}</a> : undefined}>
          {created.status === 'draft'
            ? t('In Stripe als Entwurf angelegt. Prüfen und versenden kannst du ihn dort.')
            : t('In Stripe angelegt und abgeschlossen: Rechnung %@ über %@.', created.number ?? '–', fmt.money(created.stripeTotalCents ?? created.grossCents, created.currency, 2))}
        </Banner>
      ) : null}
      {!report ? <Loading label={t('Lädt …')} /> : summaries.length === 0 ? <Banner>{t('Keine abrechenbare Zeit in diesem Zeitraum.')}</Banner> : (
        <div className="print-area paper">
          <div className="paper__head">
            <div className="paper__sender">{sender ? sender.split('\n').map((line, i) => <div key={i}>{line}</div>) : <span className="note">{t('Absender unter Einstellungen › Rechnungen')}</span>}</div>
            <div className="paper__meta">
              <strong>{t('Rechnungszusammenfassung')}</strong>
              {chosenClient ? <span>{chosenClient.name}</span> : null}
              <span>{rangeLabel(store, range)}</span>
              <span>{t('Erstellt am %@', fmt.dayMonthYear(now))}</span>
            </div>
          </div>
          {summaries.map((s) => (
            <table key={s.currency} className="data-table paper__table">
              <thead><tr><th>{t('Projekt')}</th><th className="table__num">{t('Abgerechnet')}</th><th className="table__num">{t('Gerundet')}</th>
                <th className="table__num">{t('Satz')}</th><th className="table__num">{t('Betrag')}</th></tr></thead>
              <tbody>
                {s.lines.map((line) => (
                  <tr key={line.project}>
                    <td>{name(line.project)}</td>
                    <td className="table__num">{fmt.duration(line.billedMs)}</td>
                    <td className="table__num">{`${fmt.decimalHours(line.roundedMs, 2)} h`}</td>
                    <td className="table__num">{line.rateCents !== null ? t('%@/h', fmt.money(line.rateCents, s.currency, 2)) : '–'}</td>
                    <td className="table__num">{fmt.money(line.amountCents, s.currency, 2)}</td>
                  </tr>
                ))}
                {s.tokenLines.map((line) => (
                  <tr key={`tokens-${line.project}`}>
                    <td>{t('KI-Token-Kosten · %@', name(line.project))}</td>
                    <td className="table__num">–</td>
                    <td className="table__num">–</td>
                    <td className="table__num">{fmt.money(line.usdCents, 'USD', 2)}</td>
                    <td className="table__num">{fmt.money(line.amountCents, s.currency, 2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr><td colSpan={4}>{t('Netto')}</td><td className="table__num">{fmt.money(s.netCents, s.currency, 2)}</td></tr>
                {s.vatBasisPoints === 0 ? null : (
                  <tr><td colSpan={4}>{t('Umsatzsteuer %@ %%', percent(s.vatBasisPoints))}</td><td className="table__num">{fmt.money(s.vatCents, s.currency, 2)}</td></tr>
                )}
                <tr className="paper__total">
                  <td colSpan={4}>{s.vatBasisPoints === 0 ? t('Gesamt (ohne USt.)') : t('Gesamt inkl. %@ %% USt.', percent(s.vatBasisPoints))}</td>
                  <td className="table__num">{fmt.money(s.grossCents, s.currency, 2)}</td>
                </tr>
              </tfoot>
            </table>
          ))}
          {bp === 0 ? <p className="paper__note">{t('Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.')}</p> : null}
          <p className="paper__note">{t('Abgerechnet ist die zusammengeführte Zeit pro Projekt: eigene Zeit und Agent-Zeit, Überschneidungen einmal. Gerundet wird pro Intervall, wie im Projekt eingestellt.')}</p>
          {tokenCosts && summaries.some((s) => s.tokenLines.length > 0) ? (
            <p className="paper__note">{tokenCosts.markup > 0
              ? t('KI-Token-Kosten: was die Anbieter der Agents für die Sessions im Zeitraum gemeldet haben (in US-Dollar), umgerechnet mit 1 $ = %@ zuzüglich %@ Aufschlag. Sie sind nicht Teil der Zeit.', fmt.money(tokenCosts.usdRate / 100, 'EUR', tokenCosts.usdRate % 100 === 0 ? 2 : 4), `${fmt.number(tokenCosts.markup / 100, tokenCosts.markup % 100 === 0 ? 0 : 2)} %`)
              : t('KI-Token-Kosten: was die Anbieter der Agents für die Sessions im Zeitraum gemeldet haben (in US-Dollar), umgerechnet mit 1 $ = %@. Sie sind nicht Teil der Zeit.', fmt.money(tokenCosts.usdRate / 100, 'EUR', tokenCosts.usdRate % 100 === 0 ? 2 : 4))}</p>
          ) : null}
        </div>
      )}
    </Sheet>
  );
}

export function ReportPrintSheet({ range }: { range: Interval }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const now = useNow(60_000);
  const loaded = useRange(range, true, isOwner(store.ws) && store.ui.reportTeam);
  const close = () => store.closeSheet();
  const report = loaded.data ? buildReport(t, store.ws, loaded.data, range, 'project', now, store.timeZone, false) : null;
  const bySource = loaded.data ? buildReport(t, store.ws, loaded.data, range, 'source', now, store.timeZone, false) : null;
  return (
    <Sheet title={t('Bericht als PDF')} subtitle={rangeLabel(store, range)} onClose={close} width={720} footer={<>
      <span className="spacer" />
      <Button variant="plain" onClick={close}>{t('Schließen')}</Button>
      <PrintButton />
    </>}>
      {!report || !bySource ? <Loading label={t('Lädt …')} /> : (
        <div className="print-area paper">
          <div className="paper__head">
            <div><strong className="paper__title">{t('Tellary · Bericht')}</strong><div>{rangeLabel(store, range)}</div></div>
            <div className="paper__meta"><span>{store.memberName}</span><span>{t('Erstellt am %@', fmt.dayMonthYear(now))}</span></div>
          </div>
          <div className="paper__figures">
            <Figure label={t('Abgerechnet')} value={`${fmt.decimalHours(report.total.billedMs)} h`} strong />
            <Figure label={t('Summe')} value={`${fmt.decimalHours(report.total.rawMs)} h`} />
            <Figure label={t('Eigene Zeit')} value={`${fmt.decimalHours(report.total.humanMs)} h`} />
            <Figure label={t('Agent-Zeit')} value={`${fmt.decimalHours(report.total.agentMs)} h`} />
            <Figure label={t('Abrechenbar')} value={fmt.money(report.total.amountCents)} strong />
          </div>
          <table className="data-table paper__table">
            <thead><tr><th>{t('Projekt')}</th><th className="table__num">{t('Abgerechnet')}</th><th className="table__num">{t('Summe')}</th>
              <th className="table__num">{t('Überschneidung')}</th><th className="table__num">{t('Betrag')}</th></tr></thead>
            <tbody>
              {report.rows.map((r) => (
                <tr key={r.key}><td>{r.label}</td><td className="table__num">{fmt.duration(r.totals.billedMs)}</td><td className="table__num">{fmt.duration(r.totals.rawMs)}</td>
                  <td className="table__num">{fmt.duration(Math.max(0, r.totals.rawMs - r.totals.billedMs))}</td><td className="table__num">{fmt.money(r.totals.amountCents)}</td></tr>
              ))}
            </tbody>
            <tfoot><tr className="paper__total"><td>{t('Gesamt')}</td><td className="table__num">{fmt.duration(report.total.billedMs)}</td><td className="table__num">{fmt.duration(report.total.rawMs)}</td>
              <td className="table__num">{fmt.duration(Math.max(0, report.total.rawMs - report.total.billedMs))}</td><td className="table__num">{fmt.money(report.total.amountCents)}</td></tr></tfoot>
          </table>
          <table className="data-table paper__table">
            <thead><tr><th>{t('Quelle')}</th><th className="table__num">{t('Abgerechnet')}</th><th className="table__num">{t('Summe')}</th></tr></thead>
            <tbody>{bySource.rows.map((r) => <tr key={r.key}><td>{r.label}</td><td className="table__num">{fmt.duration(r.totals.billedMs)}</td><td className="table__num">{fmt.duration(r.totals.rawMs)}</td></tr>)}</tbody>
          </table>
          <p className="paper__note">{t('Pro Projekt zusammengeführt, dann addiert: Überschneidungen zählen einmal. Quellen überschneiden sich und ergeben zusammen mehr als die abgerechnete Zeit. Agent-Zeit zählt nur, wenn der Agent arbeitet; Warten auf Eingabe oder Freigabe zählt nicht.')}</p>
        </div>
      )}
    </Sheet>
  );
}
